import { useState, useEffect, useRef } from "react"

/* ─── canvas bg ─── */
function BgCanvas() {
    const ref = useRef(null)
    useEffect(() => {
        const canvas = ref.current
        const ctx = canvas.getContext("2d")
        let raf
        const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight }
        resize()
        window.addEventListener("resize", resize)
        const orbs = [
            { x: .12, y: .15, r: 380, c: "rgba(85,34,125,",  s: .00016, p: 0 },
            { x: .88, y: .1,  r: 300, c: "rgba(255,200,0,",   s: .00022, p: 2 },
            { x: .5,  y: .6,  r: 420, c: "rgba(50,15,100,",   s: .00012, p: 4 },
            { x: .78, y: .7,  r: 240, c: "rgba(120,50,200,",  s: .0002,  p: 1.5 },
            { x: .08, y: .7,  r: 260, c: "rgba(255,160,0,",   s: .00014, p: 3.3 },
        ]
        const stars = Array.from({ length: 120 }, () => ({
            x: Math.random(), y: Math.random(),
            r: .4 + Math.random() * 1.3,
            s: .0003 + Math.random() * .0007,
            p: Math.random() * Math.PI * 2,
        }))
        const shoots = Array.from({ length: 5 }, mkShoot)
        function mkShoot() {
            return { x: Math.random(), y: Math.random() * .5, len: .06 + Math.random() * .09, spd: .0007 + Math.random() * .001, life: 0, maxLife: .6 + Math.random() * 1, angle: Math.PI / 4 + (Math.random() - .5) * .4, active: Math.random() > .65, delay: Math.random() * 10 }
        }
        let t = 0
        const draw = () => {
            const W = canvas.width, H = canvas.height
            ctx.clearRect(0, 0, W, H)
            t += .016
            orbs.forEach(o => {
                const px = (o.x + Math.sin(t * o.s * 1000 + o.p) * .09) * W
                const py = (o.y + Math.cos(t * o.s * 800 + o.p) * .07) * H
                const g = ctx.createRadialGradient(px, py, 0, px, py, o.r)
                g.addColorStop(0, o.c + ".16)"); g.addColorStop(.5, o.c + ".06)"); g.addColorStop(1, o.c + "0)")
                ctx.fillStyle = g; ctx.beginPath(); ctx.arc(px, py, o.r, 0, Math.PI * 2); ctx.fill()
            })
            const cols = 28, cw = W / cols
            for (let x = 0; x <= cols; x++) {
                ctx.strokeStyle = `rgba(255,255,255,${.016 + .008 * Math.sin(t * .3 + x * .2)})`
                ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(x * cw, 0); ctx.lineTo(x * cw, H); ctx.stroke()
            }
            for (let y = 0; y * cw < H; y++) {
                ctx.strokeStyle = `rgba(255,255,255,${.016 + .008 * Math.sin(t * .3 + y * .2)})`
                ctx.beginPath(); ctx.moveTo(0, y * cw); ctx.lineTo(W, y * cw); ctx.stroke()
            }
            for (let x = 0; x <= cols; x++) for (let y = 0; y * cw < H; y++) {
                ctx.fillStyle = `rgba(255,255,255,${.03 + .03 * Math.sin(t * .8 + x * .4 + y * .4)})`
                ctx.beginPath(); ctx.arc(x * cw, y * cw, 1.1, 0, Math.PI * 2); ctx.fill()
            }
            stars.forEach(s => {
                const a = .1 + .5 * (.5 + .5 * Math.sin(t * s.s * 1000 + s.p))
                ctx.fillStyle = `rgba(255,255,255,${a})`
                ctx.beginPath(); ctx.arc(s.x * W, s.y * H, s.r, 0, Math.PI * 2); ctx.fill()
            })
            shoots.forEach((s, i) => {
                if (!s.active) { s.delay -= .016; if (s.delay <= 0) { s.active = true; s.life = 0 }; return }
                s.life += .016
                if (s.life > s.maxLife) { shoots[i] = mkShoot(); shoots[i].active = true; return }
                const prog = s.life / s.maxLife
                const px = (s.x + Math.cos(s.angle) * s.spd * s.life * 1000) * W
                const py = (s.y + Math.sin(s.angle) * s.spd * s.life * 1000) * H
                const alpha = Math.sin(prog * Math.PI) * .95
                const ex = px - Math.cos(s.angle) * s.len * W
                const ey = py - Math.sin(s.angle) * s.len * H
                const g = ctx.createLinearGradient(ex, ey, px, py)
                g.addColorStop(0, "rgba(255,255,255,0)"); g.addColorStop(1, `rgba(255,240,160,${alpha})`)
                ctx.strokeStyle = g; ctx.lineWidth = 1.8; ctx.beginPath(); ctx.moveTo(ex, ey); ctx.lineTo(px, py); ctx.stroke()
                const hg = ctx.createRadialGradient(px, py, 0, px, py, 7)
                hg.addColorStop(0, `rgba(255,240,160,${alpha * .8})`); hg.addColorStop(1, "rgba(255,240,160,0)")
                ctx.fillStyle = hg; ctx.beginPath(); ctx.arc(px, py, 7, 0, Math.PI * 2); ctx.fill()
            })
            raf = requestAnimationFrame(draw)
        }
        draw()
        return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize) }
    }, [])
    return <canvas ref={ref} style={{ position: "fixed", inset: 0, width: "100%", height: "100%", zIndex: 0, pointerEvents: "none" }} />
}

/* ─── field label ─── */
function Field({ label, required, children }) {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ fontSize: ".72rem", fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "rgba(255,255,255,.4)" }}>
                {label}{required && <span style={{ color: "#ffe600", marginLeft: "3px" }}>*</span>}
            </label>
            {children}
        </div>
    )
}

const iBase = { width: "100%", padding: "14px 18px", borderRadius: "12px", fontSize: ".95rem", fontFamily: "'Inter',sans-serif", color: "#fff", background: "rgba(255,255,255,.07)", border: "1.5px solid rgba(255,255,255,.1)", outline: "none", transition: "border-color .2s,background .2s", boxSizing: "border-box" }

function Input({ ...props }) {
    const [f, setF] = useState(false)
    return <input {...props} style={{ ...iBase, borderColor: f ? "rgba(255,230,0,.55)" : "rgba(255,255,255,.1)", background: f ? "rgba(255,255,255,.1)" : "rgba(255,255,255,.07)" }} onFocus={() => setF(true)} onBlur={() => setF(false)} />
}
function Textarea({ ...props }) {
    const [f, setF] = useState(false)
    return <textarea {...props} style={{ ...iBase, minHeight: "108px", resize: "vertical", borderColor: f ? "rgba(255,230,0,.55)" : "rgba(255,255,255,.1)", background: f ? "rgba(255,255,255,.1)" : "rgba(255,255,255,.07)" }} onFocus={() => setF(true)} onBlur={() => setF(false)} />
}
function Sel({ children, ...props }) {
    const [f, setF] = useState(false)
    return (
        <select {...props} style={{ ...iBase, cursor: "pointer", appearance: "none", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' fill='none'%3E%3Cpath d='M1 1l5 5 5-5' stroke='rgba(255,255,255,.35)' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 16px center", paddingRight: "42px", borderColor: f ? "rgba(255,230,0,.55)" : "rgba(255,255,255,.1)", background: f ? "rgba(255,255,255,.1)" : "rgba(255,255,255,.07)" }} onFocus={() => setF(true)} onBlur={() => setF(false)}>
            {children}
        </select>
    )
}

/* ─── college list ─── */
const COLLEGES = [
    "Amity University, Lucknow",
    "Amity University, Noida",
    "Amity University, Mumbai",
    "VIT, Vellore",
    "VIT, Chennai",
    "VIT, Bhopal",
    "VIT, AP",
    "SRM Institute of Science & Technology, Chennai",
    "SRM University, Delhi-NCR",
    "SRM University, AP",
    "KIIT University, Bhubaneswar",
    "IIT Delhi",
    "IIT Bombay",
    "IIT Madras",
    "IIT Kanpur",
    "IIT Kharagpur",
    "IIT Roorkee",
    "IIT Hyderabad",
    "IIT Guwahati",
    "IIT BHU, Varanasi",
    "IIT Indore",
    "IIT Patna",
    "IIT Jodhpur",
    "IIT Mandi",
    "IIT Tirupati",
    "NIT Trichy",
    "NIT Warangal",
    "NIT Surathkal",
    "NIT Calicut",
    "NIT Rourkela",
    "NIT Allahabad",
    "NIT Jaipur",
    "NIT Kurukshetra",
    "NIT Durgapur",
    "NIT Surat",
    "BITS Pilani",
    "BITS Goa",
    "BITS Hyderabad",
    "BITS Pilani, Dubai",
    "Manipal Institute of Technology",
    "Thapar University, Patiala",
    "PES University, Bangalore",
    "Christ University, Bangalore",
    "Symbiosis Institute of Technology, Pune",
    "Vellore Institute of Technology",
    "Chandigarh University",
    "Lovely Professional University",
    "Shiv Nadar University",
    "Ashoka University",
    "O.P. Jindal Global University",
    "Bennett University",
    "Graphic Era University",
    "Jaypee Institute of Information Technology",
    "DTU (Delhi Technological University)",
    "NSUT (Netaji Subhas University of Technology)",
    "IGDTUW",
    "IIIT Delhi",
    "IIIT Hyderabad",
    "IIIT Allahabad",
    "IIIT Bangalore",
    "IIIT Pune",
    "Jadavpur University",
    "Anna University",
    "Osmania University",
    "Pune University (SPPU)",
    "Mumbai University",
    "Delhi University",
    "BHU (Banaras Hindu University)",
    "Aligarh Muslim University",
    "Jamia Millia Islamia",
    "Panjab University",
    "Calcutta University",
    "Hyderabad University",
    "Other",
]

const YEARS = ["1st Year", "2nd Year", "3rd Year", "4th Year", "Graduate"]

// Brochure links pass ?campus= so the form starts on that college.
const CAMPUSES = {
    amity: { college: "Amity University, Lucknow" },
    vit: { college: "VIT, Vellore" },
    srm: { college: "SRM Institute of Science & Technology, Chennai" },
    kiit: { college: "KIIT University, Bhubaneswar" },
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
*{box-sizing:border-box;margin:0;padding:0}
html,body{background:#08080f}

.rr{font-family:'Inter',sans-serif;background:transparent;color:#fff;min-height:100vh;position:relative;z-index:1;display:grid;grid-template-columns:1fr 380px;min-height:100svh}

/* ── left panel ── */
.rr-left{padding:48px 56px 80px;overflow-y:auto;display:flex;flex-direction:column}
.rr-chip{display:inline-flex;align-items:center;gap:8px;background:rgba(255,230,0,.1);border:1.5px solid rgba(255,230,0,.28);border-radius:100px;padding:7px 20px;font-size:.72rem;font-weight:700;letter-spacing:.1em;color:#ffe600;text-transform:uppercase;margin-bottom:20px;width:fit-content}
.rr-h1{font-size:clamp(2.2rem,4vw,3.2rem);font-weight:900;letter-spacing:-.04em;line-height:1;margin-bottom:10px}
.rr-h1 em{font-style:normal;color:#ffe600}
.rr-sub{font-size:.95rem;color:rgba(255,255,255,.42);margin-bottom:44px;line-height:1.65}

/* step track */
.step-track{display:flex;align-items:center;margin-bottom:36px;gap:0}
.step-node{display:flex;flex-direction:column;align-items:center;gap:5px;flex:1;position:relative}
.step-node:not(:last-child)::after{content:'';position:absolute;top:19px;left:calc(50% + 20px);right:calc(-50% + 20px);height:1.5px;background:var(--lb);transition:background .5s}
.step-circle{width:38px;height:38px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:.95rem;border:2px solid;transition:all .4s cubic-bezier(.22,1,.36,1);position:relative;z-index:1}
.step-lbl{font-size:.62rem;font-weight:700;text-transform:uppercase;letter-spacing:.06em;text-align:center;white-space:nowrap;transition:color .3s}

/* glass card */
.glass{background:rgba(255,255,255,.05);border:1.5px solid rgba(255,255,255,.1);border-radius:22px;backdrop-filter:blur(16px);padding:36px 36px 28px}

/* step head */
.s-eye{font-size:.68rem;font-weight:700;letter-spacing:.15em;text-transform:uppercase;color:#ffe600;margin-bottom:6px}
.s-title{font-size:1.4rem;font-weight:800;letter-spacing:-.03em;margin-bottom:3px}
.s-desc{font-size:.85rem;color:rgba(255,255,255,.4);margin-bottom:28px;padding-bottom:24px;border-bottom:1px solid rgba(255,255,255,.07)}

/* form layout */
.stack{display:flex;flex-direction:column;gap:18px}
.g2{display:grid;grid-template-columns:1fr 1fr;gap:16px}
.g3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px}

/* social */
.social-row{display:flex;align-items:flex-start;gap:12px;background:rgba(255,255,255,.04);border:1.5px solid rgba(255,255,255,.08);border-radius:14px;padding:16px;transition:border-color .2s}
.social-row:hover{border-color:rgba(255,230,0,.2)}
.social-fields{flex:1;display:flex;flex-direction:column;gap:10px}
.add-btn{width:100%;padding:13px;border-radius:12px;background:rgba(255,230,0,.07);border:1.5px dashed rgba(255,230,0,.28);color:#ffe600;font-weight:700;font-size:.88rem;cursor:pointer;transition:all .2s;font-family:'Inter',sans-serif;letter-spacing:.02em}
.add-btn:hover{background:rgba(255,230,0,.13);border-color:rgba(255,230,0,.5)}
.rm-btn{padding:10px 11px;border-radius:9px;background:rgba(239,68,68,.12);border:1.5px solid rgba(239,68,68,.25);color:#f87171;cursor:pointer;font-size:.75rem;font-weight:700;transition:all .2s;flex-shrink:0;font-family:'Inter',sans-serif;line-height:1}
.rm-btn:hover{background:rgba(239,68,68,.22)}

/* nav */
.nav{display:flex;justify-content:space-between;align-items:center;padding-top:24px;margin-top:24px;border-top:1px solid rgba(255,255,255,.07)}
.btn-back{padding:13px 26px;border-radius:100px;background:rgba(255,255,255,.07);border:1.5px solid rgba(255,255,255,.1);color:rgba(255,255,255,.55);font-weight:700;font-size:.88rem;cursor:pointer;transition:all .2s;font-family:'Inter',sans-serif}
.btn-back:hover:not(:disabled){background:rgba(255,255,255,.12);color:#fff;border-color:rgba(255,255,255,.2)}
.btn-back:disabled{opacity:.25;cursor:not-allowed}
.btn-next{padding:14px 34px;border-radius:100px;background:linear-gradient(135deg,#ffe600,#ffb800);color:#000;font-weight:800;font-size:.95rem;cursor:pointer;border:none;transition:transform .2s cubic-bezier(.34,1.56,.64,1),box-shadow .2s;box-shadow:0 4px 24px rgba(255,200,0,.22);font-family:'Inter',sans-serif}
.btn-next:hover:not(:disabled){transform:scale(1.06) translateY(-2px);box-shadow:0 12px 36px rgba(255,200,0,.38)}
.btn-next:disabled{opacity:.35;cursor:not-allowed;transform:none}

/* progress ring */
.pring{position:relative;width:48px;height:48px;flex-shrink:0}
.pring svg{transform:rotate(-90deg)}
.pring .pct{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:.6rem;font-weight:800;color:#ffe600}

/* slide anim */
.slide{animation:slideIn .35s cubic-bezier(.22,1,.36,1)}
@keyframes slideIn{from{opacity:0;transform:translateX(20px)}to{opacity:1;transform:none}}

/* ── right panel ── */
.rr-right{border-left:1px solid rgba(255,255,255,.07);background:rgba(8,8,15,.6);backdrop-filter:blur(24px);display:flex;flex-direction:column;padding:48px 32px;position:sticky;top:0;height:100svh;overflow-y:auto}
.rr-right h3{font-size:.65rem;font-weight:700;letter-spacing:.2em;text-transform:uppercase;color:rgba(255,255,255,.28);margin-bottom:20px}

/* tier cards */
.tier-stack{display:flex;flex-direction:column;gap:8px}
.tier-card{display:flex;gap:12px;align-items:center;padding:12px 14px;border-radius:14px;background:rgba(255,255,255,.04);border:1.5px solid rgba(255,255,255,.07);cursor:default;transition:transform .3s cubic-bezier(.22,1,.36,1),border-color .3s,background .3s}
.tier-card:hover{background:rgba(255,255,255,.08);transform:translateX(5px)}
.tier-icon-wrap{width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:1rem;flex-shrink:0;transition:box-shadow .3s}
.tier-body{flex:1;min-width:0}
.tier-top-row{display:flex;align-items:center;justify-content:space-between;gap:6px;margin-bottom:3px}
.tier-persona{font-size:.58rem;font-weight:800;letter-spacing:.08em;text-transform:uppercase;padding:2px 7px;border-radius:100px;white-space:nowrap;line-height:1.6}
.tier-cash{font-size:.92rem;font-weight:900;letter-spacing:-.02em;flex-shrink:0}
.tier-label{font-size:.8rem;font-weight:700;color:rgba(255,255,255,.8);margin-bottom:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.tier-bottom-row{display:flex;align-items:center;gap:6px}
.tier-refs-pill{font-size:.62rem;font-weight:700;color:rgba(255,255,255,.32);background:rgba(255,255,255,.06);padding:1px 6px;border-radius:100px;white-space:nowrap;flex-shrink:0}
.tier-desc{font-size:.67rem;color:rgba(255,255,255,.28);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}

/* total callout */
.total-callout{border-radius:14px;padding:16px 18px;background:linear-gradient(135deg,rgba(255,200,0,.08),rgba(255,140,0,.04));border:1.5px solid rgba(255,200,0,.22);position:relative;overflow:hidden;margin-top:14px}
.total-callout::after{content:'';position:absolute;top:-20px;right:-10px;width:80px;height:80px;border-radius:50%;background:rgba(255,200,0,.07);filter:blur(16px);pointer-events:none}
.total-label{font-size:.6rem;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:rgba(255,200,0,.55);margin-bottom:3px}
.total-val{font-size:1.7rem;font-weight:900;color:#ffe600;letter-spacing:-.04em;line-height:1}
.total-sub{font-size:.68rem;color:rgba(255,255,255,.3);margin-top:3px}

.divider-line{height:1px;background:linear-gradient(to right,transparent,rgba(255,255,255,.08),transparent);margin:24px 0}

.info-block{background:rgba(255,230,0,.06);border:1.5px solid rgba(255,230,0,.18);border-radius:16px;padding:20px}
.info-block h4{font-size:.72rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#ffe600;margin-bottom:12px}
.info-item{display:flex;align-items:flex-start;gap:10px;margin-bottom:10px}
.info-item:last-child{margin-bottom:0}
.info-icon{font-size:1rem;flex-shrink:0;margin-top:1px}
.info-text{font-size:.82rem;color:rgba(255,255,255,.5);line-height:1.55}

/* success */
.success{position:fixed;inset:0;background:rgba(8,8,15,.96);z-index:100;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:40px;animation:fadeIn .4s ease}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
.success-icon{font-size:5rem;margin-bottom:24px;animation:pop .6s .2s both cubic-bezier(.34,1.56,.64,1)}
@keyframes pop{from{opacity:0;transform:scale(.3)}to{opacity:1;transform:scale(1)}}
.success h2{font-size:clamp(1.8rem,5vw,3rem);font-weight:900;letter-spacing:-.04em;margin-bottom:14px}
.success h2 em{color:#ffe600;font-style:normal}
.success p{color:rgba(255,255,255,.48);font-size:1rem;line-height:1.65;max-width:400px;margin-bottom:36px}

/* year chips */
.chips{display:flex;flex-wrap:wrap;gap:8px}
.ychip{padding:11px 18px;border-radius:100px;background:rgba(255,255,255,.07);border:1.5px solid rgba(255,255,255,.1);color:rgba(255,255,255,.7);font-weight:700;font-size:.86rem;cursor:pointer;font-family:'Inter',sans-serif;transition:all .2s}
.ychip:hover{border-color:rgba(255,230,0,.4)}
.ychip.on{background:rgba(255,230,0,.14);border-color:#ffe600;color:#ffe600}
.opt{font-size:.62rem;font-weight:600;letter-spacing:.06em;color:rgba(255,255,255,.28);margin-left:6px;text-transform:none}
.stipend-box{border-radius:16px;padding:22px 20px;background:linear-gradient(135deg,rgba(255,200,0,.1),rgba(255,140,0,.04));border:1.5px solid rgba(255,200,0,.25)}
.stipend-val{font-size:2.2rem;font-weight:900;color:#ffe600;letter-spacing:-.04em;line-height:1}
.stipend-sub{font-size:.8rem;color:rgba(255,255,255,.55);margin-top:6px;line-height:1.5}
.resp-mini{display:flex;flex-direction:column;gap:9px;margin-top:4px}
.resp-mini div{font-size:.8rem;color:rgba(255,255,255,.55);line-height:1.5;padding-left:16px;position:relative}
.resp-mini div::before{content:'';position:absolute;left:0;top:8px;width:6px;height:6px;border-radius:50%;background:#ffe600}

/* stipend strip: phones, where the side panel is hidden */
.m-stipend{display:none;align-items:center;gap:14px;border-radius:16px;padding:14px 18px;margin-bottom:20px;background:linear-gradient(135deg,rgba(255,200,0,.12),rgba(255,140,0,.05));border:1.5px solid rgba(255,200,0,.28)}
.m-stipend b{font-size:1.5rem;font-weight:900;color:#ffe600;letter-spacing:-.03em;white-space:nowrap}
.m-stipend span{font-size:.8rem;color:rgba(255,255,255,.6);line-height:1.45}

/* select options */
select option{background:#130d1f;color:#fff}

/* email error modal */
.em-overlay{position:fixed;inset:0;background:rgba(0,0,0,.72);z-index:200;display:flex;align-items:center;justify-content:center;padding:24px;animation:fadeIn .2s ease;backdrop-filter:blur(6px)}
.em-box{background:#0f0a1e;border:1.5px solid rgba(239,68,68,.35);border-radius:24px;padding:40px 36px;max-width:420px;width:100%;text-align:center;position:relative;box-shadow:0 40px 100px rgba(0,0,0,.6),0 0 60px rgba(239,68,68,.08);animation:popIn .3s cubic-bezier(.34,1.56,.64,1)}
@keyframes popIn{from{opacity:0;transform:scale(.85)}to{opacity:1;transform:scale(1)}}
.em-icon{font-size:3.2rem;margin-bottom:20px;display:block}
.em-title{font-size:1.35rem;font-weight:900;letter-spacing:-.03em;margin-bottom:10px}
.em-body{font-size:.9rem;color:rgba(255,255,255,.48);line-height:1.65;margin-bottom:28px}
.em-app-link{display:inline-block;padding:13px 30px;border-radius:100px;background:linear-gradient(135deg,#ffe600,#ffb800);color:#000;font-weight:800;font-size:.9rem;text-decoration:none;margin-bottom:12px;transition:transform .2s}
.em-app-link:hover{transform:scale(1.05)}
.em-dismiss{display:block;width:100%;padding:12px;border-radius:100px;background:rgba(255,255,255,.06);border:1.5px solid rgba(255,255,255,.1);color:rgba(255,255,255,.5);font-weight:700;font-size:.88rem;cursor:pointer;font-family:'Inter',sans-serif;transition:all .2s}
.em-dismiss:hover{background:rgba(255,255,255,.11);color:#fff}

@media(max-width:900px){
    .rr{grid-template-columns:1fr}
    .rr-right{display:none}
    .m-stipend{display:flex}
    .rr-left{padding:36px 24px 80px}
    .g2,.g3{grid-template-columns:1fr}
    .step-lbl{display:none}
}
`

export default function CampusRegistration() {
    const campus = CAMPUSES[new URLSearchParams(window.location.search).get("campus")?.toLowerCase()]
    const [submitted, setSubmitted] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState("")
    const [form, setForm] = useState({
        name: "", phone: "", email: "",
        college: campus?.college || "", otherCollege: "", year: "",
        course: "", social: "", why: "",
    })

    const set = e => { setError(""); setForm(p => ({ ...p, [e.target.name]: e.target.value })) }
    const college = form.college === "Other" ? form.otherCollege.trim() : form.college

    const validate = () => {
        if (!form.name.trim()) return "Please enter your name."
        if (form.phone.replace(/\D/g, "").length < 10) return "Please enter your WhatsApp number."
        if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return "Please enter a valid email."
        if (!college) return "Please choose your college."
        if (!form.year) return "Please pick your year of study."
        return null
    }

    const handleSubmit = async () => {
        const err = validate()
        if (err) { setError(err); return }
        setSubmitting(true)
        const link = form.social.trim()
        const body = {
            display_name: form.name.trim(), email_address: form.email.trim().toLowerCase(), phone_number: form.phone.trim(),
            college_name: college, course_name: form.course.trim(), student_status: form.year,
            registered_from: "Website", why_you: form.why.trim(),
            platforms: link ? [{ platform_type: /linkedin/i.test(link) ? "linkedin" : /instagram/i.test(link) ? "instagram" : "other", platform_link: link }] : [],
        }
        try {
            const tr = await fetch("https://app.bloxsocial.live/token/jwt_token/", { headers: { "Content-Type": "application/json" } })
            if (!tr.ok) throw new Error("token")
            const { token } = await tr.json()
            const r = await fetch("https://app.bloxsocial.live/ambassador/web/register_ambassador/", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify(body),
            })
            if (r.ok) setSubmitted(true)
            else if (r.status === 409) setError("You've already applied with this email or number. We'll be in touch soon!")
            else setError("Something went wrong — please try again.")
        } catch { setError("Network error — check your connection and try again.") }
        finally { setSubmitting(false) }
    }

    if (submitted) return (
        <div className="success">
            <style>{CSS}</style>
            <BgCanvas />
            <div className="success-icon" style={{ position: "relative", zIndex: 1 }}>🎉</div>
            <h2 style={{ position: "relative", zIndex: 1 }}><em>Application received.</em></h2>
            <p style={{ position: "relative", zIndex: 1 }}>Thanks, {form.name.trim().split(" ")[0]}! If you're shortlisted, we'll message you on WhatsApp to set up a short interview.</p>
            <a href="/" style={{ position: "relative", zIndex: 1, display: "inline-block", padding: "14px 32px", borderRadius: "100px", background: "linear-gradient(135deg,#ffe600,#ffb800)", color: "#000", fontWeight: 800, fontSize: ".95rem", textDecoration: "none" }}>Back to Home</a>
        </div>
    )

    const Opt = () => <span className="opt">(optional)</span>

    return (
        <div className="rr">
            <style>{CSS}</style>
            <BgCanvas />

            {/* ── left: form ── */}
            <div className="rr-left">
                <div className="rr-chip">⚡ Campus Ambassador Application</div>
                <h1 className="rr-h1">Apply in<br /><em>one minute.</em></h1>
                <p className="rr-sub">Just the basics for now — we'll get to know you properly in a short interview.</p>

                <div className="m-stipend">
                    <b>₹5,000/mo</b>
                    <span>Fixed stipend · 3-month pilot · no referral quota</span>
                </div>

                <div className="glass">
                    <form onSubmit={e => { e.preventDefault(); handleSubmit() }}>
                        <div className="stack">
                            <Field label="Full Name" required>
                                <Input type="text" name="name" value={form.name} onChange={set} placeholder="e.g. Rahul Sharma" autoComplete="name" />
                            </Field>
                            <div className="g2">
                                <Field label="WhatsApp Number" required>
                                    <Input type="tel" name="phone" value={form.phone} onChange={set} placeholder="+91 98765 43210" autoComplete="tel" />
                                </Field>
                                <Field label="Email" required>
                                    <Input type="email" name="email" value={form.email} onChange={set} placeholder="you@gmail.com" autoComplete="email" />
                                </Field>
                            </div>
                            <Field label="College" required>
                                <Sel name="college" value={form.college} onChange={set}>
                                    <option value="">Select your college</option>
                                    {COLLEGES.map(c => <option key={c} value={c}>{c}</option>)}
                                </Sel>
                            </Field>
                            {form.college === "Other" && (
                                <Field label="College Name" required>
                                    <Input type="text" name="otherCollege" value={form.otherCollege} onChange={set} placeholder="Enter your college name" />
                                </Field>
                            )}
                            <Field label="Year of Study" required>
                                <div className="chips">
                                    {YEARS.map(y => (
                                        <button key={y} type="button" className={`ychip${form.year === y ? " on" : ""}`}
                                            onClick={() => { setError(""); setForm(p => ({ ...p, year: y })) }}>{y}</button>
                                    ))}
                                </div>
                            </Field>
                            <div className="g2">
                                <Field label={<>Course <Opt /></>}>
                                    <Input type="text" name="course" value={form.course} onChange={set} placeholder="e.g. B.Tech CSE" />
                                </Field>
                                <Field label={<>Instagram or LinkedIn <Opt /></>}>
                                    <Input type="text" name="social" value={form.social} onChange={set} placeholder="Profile link or @handle" />
                                </Field>
                            </div>
                            <Field label={<>Why you'd be great <Opt /></>}>
                                <Textarea name="why" value={form.why} onChange={set} placeholder="A line or two is plenty." />
                            </Field>
                        </div>
                        {error && (
                            <div style={{ marginTop: "16px", padding: "12px 16px", borderRadius: "12px", background: "rgba(239,68,68,.1)", border: "1.5px solid rgba(239,68,68,.3)", color: "#fca5a5", fontSize: ".85rem", fontWeight: 600, lineHeight: 1.5 }}>
                                ⚠️ {error}
                            </div>
                        )}
                        <div className="nav" style={{ justifyContent: "flex-end" }}>
                            <button type="submit" className="btn-next" disabled={submitting}>{submitting ? "Submitting…" : "Submit Application →"}</button>
                        </div>
                    </form>
                </div>
            </div>

            {/* ── right panel ── */}
            <div className="rr-right">
                <h3>What you get</h3>
                <div className="stipend-box">
                    <div className="stipend-val">₹5,000<span style={{ fontSize: "1rem", fontWeight: 700 }}> / month</span></div>
                    <div className="stipend-sub">Fixed stipend for the 3-month pilot. Not tied to sign-ups or any referral quota.</div>
                </div>
                <div className="divider-line" />
                <h3>What you'll do</h3>
                <div className="resp-mini">
                    <div>Create 4–6 useful pieces of campus content</div>
                    <div>Run one approved campus activation</div>
                    <div>Introduce Blox in student communities, with permission</div>
                    <div>Help students understand and join the app</div>
                    <div>Keep the campus community on Blox active and welcoming</div>
                    <div>Send a weekly feedback update and join a weekly call</div>
                </div>
                    

                <div className="divider-line" />

                <div className="info-block">
                    <h4>What happens next</h4>
                    <div className="info-item">
                        <span className="info-icon">⏱</span>
                        <span className="info-text">We review every application within 48 hours.</span>
                    </div>
                    <div className="info-item">
                        <span className="info-icon">💬</span>
                        <span className="info-text">Shortlisted students get a WhatsApp message to book a short interview.</span>
                    </div>
                    <div className="info-item">
                        <span className="info-icon">🏅</span>
                        <span className="info-text">Selected ambassadors get a permanent Founding Member badge.</span>
                    </div>
                </div>

                <div style={{ fontSize: ".78rem", color: "rgba(255,255,255,.25)", lineHeight: 1.6, marginTop: "20px" }}>
                    Only 5 positions per campus.
                </div>
            </div>
        </div>
    )
}
